// =========================================================================
// Rendering the seed's inline SVG glyphs as real elements.
//
// `seed/modules.ts` and `seed/connectors.ts` carry each module's and each
// connector's mark as the demo wrote it — the *children* of a `0 0 24 24`
// viewBox, as a string:
//
//     icon: '<path d="M3 17h18M5 17V9l4-3h6l4 3v8"/><circle cx="8" cy="17" r="2"/>…'
//
// The obvious way to render that is `dangerouslySetInnerHTML`, and the
// obvious way is wrong twice over. `scripts/oem/check-no-handler-strings.mjs`
// rejects it by name, and it deserves to: injecting markup from a data file
// is exactly the habit the whole port exists to break, and it would keep
// working right up until somebody put an `<image>` or an `onload` in a seed.
//
// So the string is parsed instead. The vocabulary is small and closed — all
// thirty-one glyphs in the two seeds use three elements and nine numeric
// attributes between them — which makes a strict parser both short and
// complete. Anything outside that vocabulary is **dropped**, not rendered:
// an unknown element or attribute is a seed that has drifted, and the right
// answer is a missing glyph somebody notices, not markup nobody vetted.
//
// The parse is memoised per string. Thirty-one glyphs, parsed at most once
// each per session, is not a cost worth thinking about twice.
//
// UI layer, with the parse split out as a pure function.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { useMemo } from 'react';
import type { CSSProperties } from 'react';

/** One parsed glyph child. Three shapes; the seeds use no others. */
export type GlyphShape =
  | { readonly kind: 'path'; readonly d: string }
  | { readonly kind: 'circle'; readonly cx: number; readonly cy: number; readonly r: number }
  | {
      readonly kind: 'rect';
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
      readonly rx?: number;
    };

/** `<path d="…"/>`, `<circle …/>` or `<rect …/>`, self-closing or not. */
const ELEMENT = /<(path|circle|rect)\b([^>]*)\/?>/g;

/** Read one numeric attribute, or `null` when it is absent or not a number. */
function num(attrs: string, name: string): number | null {
  const m = attrs.match(new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`));
  if (!m) return null;
  const v = Number(m[1]);
  return Number.isFinite(v) ? v : null;
}

/** Read the `d` attribute, rejecting anything with a quote or angle bracket. */
function pathData(attrs: string): string | null {
  const m = attrs.match(/\bd\s*=\s*"([^"<>]*)"/);
  return m && m[1].trim() !== '' ? m[1] : null;
}

/**
 * Parse a seed glyph into typed shapes.
 *
 * Pure and total: a malformed string yields an empty list rather than
 * throwing, because a glyph is never worth taking a page down for.
 *
 * @param markup - the seed's `icon` string: viewBox children, no `<svg>` wrapper.
 */
export function parseGlyph(markup: string): readonly GlyphShape[] {
  const shapes: GlyphShape[] = [];
  ELEMENT.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = ELEMENT.exec(markup)) !== null) {
    const [, tag, attrs] = match;

    if (tag === 'path') {
      const d = pathData(attrs);
      if (d !== null) shapes.push({ kind: 'path', d });
      continue;
    }

    if (tag === 'circle') {
      const cx = num(attrs, 'cx');
      const cy = num(attrs, 'cy');
      const r = num(attrs, 'r');
      if (cx !== null && cy !== null && r !== null) shapes.push({ kind: 'circle', cx, cy, r });
      continue;
    }

    const x = num(attrs, 'x');
    const y = num(attrs, 'y');
    const width = num(attrs, 'width');
    const height = num(attrs, 'height');
    if (x === null || y === null || width === null || height === null) continue;
    const rx = num(attrs, 'rx');
    shapes.push(rx === null ? { kind: 'rect', x, y, width, height } : { kind: 'rect', x, y, width, height, rx });
  }

  return shapes;
}

export interface SeedGlyphProps {
  /** The seed's `icon` string. */
  markup: string;
  className?: string;
  /** Stroke colour. Defaults to `currentColor`, which is usually what you want. */
  stroke?: string;
  strokeWidth?: number;
  style?: CSSProperties;
}

/**
 * A seed glyph on the demo's `0 0 24 24` viewBox.
 *
 * Always `aria-hidden`: every glyph in this lane sits beside its own label,
 * so announcing it would read the name twice.
 */
export function SeedGlyph({
  markup,
  className,
  stroke = 'currentColor',
  strokeWidth = 1.7,
  style,
}: SeedGlyphProps) {
  const shapes = useMemo(() => parseGlyph(markup), [markup]);
  if (shapes.length === 0) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={{
        stroke,
        fill: 'none',
        strokeWidth,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        ...style,
      }}
    >
      {shapes.map((shape, i) => {
        switch (shape.kind) {
          case 'path':
            return <path key={i} d={shape.d} />;
          case 'circle':
            return <circle key={i} cx={shape.cx} cy={shape.cy} r={shape.r} />;
          case 'rect':
            return (
              <rect
                key={i}
                x={shape.x}
                y={shape.y}
                width={shape.width}
                height={shape.height}
                rx={shape.rx}
              />
            );
        }
      })}
    </svg>
  );
}
